import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  IncentivesManagerFetchInterface,
  IncentivesManagerFetchWriteInterface
} from "@dealer-center/ddc-pages-incentives-manager-interface";

import { isHttpError } from "@dealer-center/ddc-pages-incentives-manager-interface";

const queryKeyBase = "ddc-pages-incentives-manager";

export function useReadAPI({
  resource = "",
  resourceId = "",
  asAccount = "",
  asUser = "",
  locale = "en_US",
  extraURLParams = "",
  isMock = false,
  disabled = false
}: IncentivesManagerFetchInterface) {
  return useQuery({
    queryKey: [queryKeyBase, resource, resourceId],
    queryFn: async () => {
      const rstr = resource ? `/${resource}` : "";
      const ridstr = resourceId ? `/${resourceId}` : "";
      const mockstr = isMock ? "&mock" : "";

      const res = await fetch(
        `/landing/nxlambda/incentivesmanager${rstr}${ridstr}?asAccount=${asAccount}&asUser=${asUser}&locale=${locale}${mockstr}${extraURLParams}`
      );
      if (res.ok) {
        return await res.json();
      } else {
        throw new Error(res.statusText);
      }
    },
    enabled: !disabled,
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
}

export function useWriteAPI({
  resource,
  asAccount,
  asUser,
  locale,
  afterSuccess
}: IncentivesManagerFetchWriteInterface) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      resourceId,
      body,
      extraURLParams = ""
    }: {
      resourceId?: string;
      body?: object;
      extraURLParams?: string;
    }) => {
      const rstr = resource ? `/${resource}` : "";
      const ridstr = resourceId ? `/${resourceId}` : "";
      const res = await fetch(
        `/landing/nxlambda/incentivesmanager${rstr}${ridstr}?asAccount=${asAccount}&asUser=${asUser}&locale=${locale}&${extraURLParams}`,
        {
          method: "PUT",
          body: body ? JSON.stringify(body) : "",
          headers: { "Content-Type": "application/json" }
        }
      );
      if (res.ok) {
        const json = await res.json();
        if (isHttpError(json)) {
          throw json;
        }
        return json;
      } else {
        throw new Error(res.statusText);
      }
    },
    onSuccess: res => {
      if (afterSuccess) {
        if (typeof afterSuccess === "string") {
          queryClient.invalidateQueries({
            queryKey: [queryKeyBase, afterSuccess]
          });
        } else {
          afterSuccess(res?.body);
        }
      }
    }
  });
}
